import { useSwipeable } from "react-swipeable";
import { useNavigate } from "react-router-dom";

const MainDinner = ({ language }: { language: string }) => {
    const navigate = useNavigate();

    const handlers = useSwipeable({
        onSwipedLeft: () => navigate("/DessertDinner"),
        onSwipedRight: () => navigate("/EntreesDinner"),
        trackMouse: true,
        delta: 50,
        trackTouch: true
    });

    const renderMenuSection = (
        titleFr: string,
        titleEn: string,
        items: any[],
        subtitleFr?: string,
        subtitleEn?: string
    ) => (
        <div className="bg-white/80 backdrop-blur-sm rounded-xl p-8 max-w-4xl w-full shadow-lg mt-8">
            <h2 className="text-3xl font-semibold mb-2 text-green-950 text-center border-b pb-2">
                {language === "fr" ? titleFr : titleEn}
            </h2>
            {subtitleFr && subtitleEn && (
                <p className="text-gray-600 mb-6 italic">
                    {language === "fr" ? subtitleFr : subtitleEn}
                </p>
            )}
            <ul className="space-y-6">
                {items.map((item, index) => (
                    <MenuListItem
                        key={`${titleEn}-${index}`}
                        fr={item.fr}
                        en={item.en}
                        prixFr={item.prixFr}
                        prixEn={item.prixEn}
                        descFr={item.descFr}
                        descEn={item.descEn}
                        language={language}
                    />
                ))}
            </ul>
        </div>
    );

    return (
        <div
            {...handlers}
            className="min-h-screen flex flex-col items-center p-8 bg-cover bg-center bg-fixed pt-[110px]"
            style={{ backgroundImage: "url('/image/Lona2.jpg')" }}
        >
            <p className="text-center text-xs text-green-950 mt-2">
                {language === "fr" ? "Glissez pour naviguer" : "Swipe to navigate"}
            </p>

            {/* SALADES */}
            {renderMenuSection("SALADES", "SALADS", [
                {
                    fr: "Salade méditerranéenne au thon",
                    en: "Mediterranean Tuna Salad",
                    prixFr: "19$",
                    prixEn: "$19",
                    descFr:
                        "Salade fraîche et colorée avec thon, tomates, concombres, poivrons, olives et oignons rouges, assaisonnée d'huile d'olive et de citron.",
                    descEn:
                        "Fresh and colorful salad with tuna, tomatoes, cucumbers, peppers, olives, and red onions, dressed with olive oil and lemon.",
                },
                {
                    fr: "Horiatiki",
                    en: "Horiatiki",
                    prixFr: "18$",
                    prixEn: "$18",
                    descFr:
                        "Salade grecque traditionnelle composée de tomates, concombres, poivrons, oignons rouges, olives Kalamata et feta crémeuse, relevée d'origan et d'huile d'olive.",
                    descEn:
                        "Traditional Greek salad with tomatoes, cucumbers, peppers, red onions, Kalamata olives, and creamy feta, topped with oregano and olive oil.",
                },
                {
                    fr: "Chou frisé et haricots blancs",
                    en: "Kale & White Beans",
                    prixFr: "17$",
                    prixEn: "$17",
                    descFr:
                        "Mélange nourrissant de chou frisé et haricots blancs mijotés avec ail, citron et huile d’olive, servi tiède.",
                    descEn:
                        "Hearty mix of kale and white beans simmered with garlic, lemon, and olive oil, served warm.",
                },
            ])}

            {/* SPÉCIALITÉS */}
            {renderMenuSection("SPÉCIALITÉS", "SPECIALTIES", [
                {
                    fr: "Agneau BBQ",
                    en: "BBQ Lamb",
                    prixFr: "29$",
                    prixEn: "$29",
                    descFr:
                        "Agneau cuit au charbon servi avec une somptueuse sauce aux champignons et pommes de terre, assaisonnée avec de fraîches herbes, ail et jus de citron, accompagné de nos patates maison.",
                    descEn:
                        "Charcoal-cooked lamb served with a sumptuous mushroom and potato sauce, seasoned with fresh herbs, garlic, and lemon juice, accompanied by our homemade potatoes.",
                },
                {
                    fr: "Roulot d’aubergine",
                    en: "Eggplant Roll",
                    prixFr: "24$",
                    prixEn: "$24",
                    descFr:
                        "Tranches d’aubergine grillées farcies de bœuf haché parfumé et feta crémeuse, nappées d’une sauce tomate maison.",
                    descEn:
                        "Grilled eggplant slices stuffed with seasoned ground beef and creamy feta, topped with homemade tomato sauce.",
                },
                {
                    fr: "Caponata sicilienne",
                    en: "Sicilian Caponata",
                    prixFr: "22$",
                    prixEn: "$22",
                    descFr:
                        "Ragoût sicilien d’aubergines, tomates, céleri, olives et câpres mijotés dans une sauce douce-acidulée.",
                    descEn:
                        "Sicilian stew of eggplants, tomatoes, celery, olives, and capers simmered in a sweet-and-tangy sauce.",
                },
                {
                    fr: "Maakouda au poulet",
                    en: "Chicken Maakouda",
                    prixFr: "25$",
                    prixEn: "$25",
                    descFr:
                        "Délicates boulettes de maakouda aux épices fines, mijotées avec des éclats de poulet tendre dans une sauce blanche crémeuse parfumée aux herbes méditerranéennes.",
                    descEn:
                        "Delicate spiced maakouda simmered with tender pieces of chicken in a creamy white sauce infused with Mediterranean herbs.",
                }
                ,
                {
                    fr: "Vol-au-vent au bœuf",
                    en: "Beef Vol-au-vent",
                    prixFr: "26$",
                    prixEn: "$26",
                    descFr:
                        "Feuilleté doré garni de bœuf mijoté avec champignons, olives et une sauce onctueuse.",
                    descEn:
                        "Golden puff pastry filled with beef simmered with mushrooms, olives, and a creamy sauce.",
                }
                ,
            ])}
        </div>
    );
};

const MenuListItem = ({ fr, en, prixFr, prixEn, descFr, descEn, language }: any) => (
    <li className="border-b border-gray-200 pb-6 last:border-0">
        <div className="flex justify-between items-start">
            <div>
                <h3 className="font-bold text-xl">{language === "fr" ? fr : en}</h3>
                <p className="text-gray-600 mt-1 text-sm">{language === "fr" ? descFr : descEn}</p>
            </div>
            <span className="text-green-950 font-medium text-lg whitespace-nowrap ml-4">
                {language === "fr" ? prixFr : prixEn}
            </span>
        </div>
    </li>
);

export default MainDinner;
